import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Upload, X } from "lucide-react";
import { api, mediaUrl } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";

const CACHE = "gallery_cache_v1";

export default function Gallery() {
  const { t } = useLang();
  const [items, setItems] = useState(() => JSON.parse(localStorage.getItem(CACHE) || "null") || []);
  const [loading, setLoading] = useState(items.length === 0);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/gallery").then((r) => {
    setItems(r.data);
    localStorage.setItem(CACHE, JSON.stringify(r.data));
  }).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 fade-up">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div className="max-w-2xl">
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-stone-900">{t("gallery_title")}</h1>
          <p className="mt-2 text-stone-600 leading-relaxed">{t("gallery_sub")}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="gallery-add-btn" className="rounded-full bg-[#2E604A] hover:bg-[#234B3A] gap-2 h-11 px-6 shrink-0">
              <ImagePlus className="w-4 h-4" /> {t("gallery_add")}
            </Button>
          </DialogTrigger>
          <UploadDialog onDone={() => { setOpen(false); load(); }} />
        </Dialog>
      </div>

      {loading && items.length === 0 ? (
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 [column-fill:_balance]">
          {[...Array(6)].map((_, i) => <Skeleton key={`gs-${i}`} className="h-56 rounded-2xl mb-4" />)}
        </div>
      ) : items.length === 0 ? (
        <p className="text-center py-20 text-stone-400" data-testid="gallery-empty">No photos yet.</p>
      ) : (
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 [column-fill:_balance]" data-testid="gallery-grid">
          {items.map((item) => (
            <figure key={item.id} data-testid={`gallery-item-${item.id}`}
              className="mb-4 break-inside-avoid rounded-2xl overflow-hidden border border-stone-200 bg-white shadow-sm card-lift">
              <img
                src={mediaUrl(item.image_url)}
                alt={item.title}
                loading="lazy"
                decoding="async"
                className="w-full h-auto object-cover"
              />
              <figcaption className="p-4">
                <p className="font-display font-bold text-stone-900 text-sm">{item.title}</p>
                {item.caption && <p className="text-sm text-stone-500 mt-1 leading-relaxed">{item.caption}</p>}
              </figcaption>
            </figure>
          ))}
        </div>
      )}
    </div>
  );
}

function UploadDialog({ onDone }) {
  const { t } = useLang();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [title, setTitle] = useState("");
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("Image is too large (max 8 MB)."); return; }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async () => {
    if (!file) { toast.error("Please choose a photo."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title);
      fd.append("caption", caption);
      await api.post("/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("gallery_ok"));
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="rounded-2xl" data-testid="upload-dialog">
      <DialogHeader><DialogTitle className="font-display text-start">{t("gallery_add")}</DialogTitle></DialogHeader>
      <div className="space-y-4">
        <label className="block cursor-pointer">
          <input type="file" accept="image/*" onChange={pick} className="hidden" data-testid="upload-file" />
          {preview ? (
            <div className="relative rounded-xl overflow-hidden border border-stone-200">
              <img src={preview} alt="preview" className="w-full h-48 object-cover" />
              <span className="absolute top-2 right-2 bg-white/90 rounded-full p-1"><X className="w-4 h-4 text-stone-700" onClick={(e) => { e.preventDefault(); setFile(null); setPreview(""); }} /></span>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-stone-300 h-40 text-stone-500 hover:border-[#2E604A] transition-colors">
              <Upload className="w-6 h-6" />
              <span className="text-sm font-semibold">{t("gallery_choose")}</span>
            </div>
          )}
        </label>
        <div>
          <Label className="text-sm font-semibold mb-1.5 block">{t("g_title_label")}</Label>
          <Input data-testid="upload-title" value={title} onChange={(e) => setTitle(e.target.value)} className="rounded-xl h-11" />
        </div>
        <div>
          <Label className="text-sm font-semibold mb-1.5 block">{t("g_caption_label")}</Label>
          <Input data-testid="upload-caption" value={caption} onChange={(e) => setCaption(e.target.value)} className="rounded-xl h-11" />
        </div>
      </div>
      <DialogFooter>
        <Button data-testid="upload-submit" onClick={submit} disabled={busy}
          className="rounded-full bg-[#2E604A] hover:bg-[#234B3A] gap-2 w-full h-11">
          <Upload className="w-4 h-4" /> {busy ? t("gallery_uploading") : t("gallery_upload")}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
